import React, { useEffect, useState } from 'react';
import { withRouter } from 'found';
import { routes } from '../../constants';
import services from '../../services';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { login } from '../../actions/auth.actions';
import { connect } from 'react-redux';
import RedirectIfLoggedIn from '../../router/redirect/RedirectIfLoggedIn';
import { Box, Button, FormControl } from '@mui/material';
import { spaces } from '../../assets/styles/themes/tokens';
import FooterLegalNavigation from '../../components/login/FooterLegalNavigation';
import PageContainer from '../../components/login/PageContainer';
import OnboardingHeader from '../../components/onboarding/OnboardingHeader';
import useRouter from '../../utils/useRouter';
import useScreenSize from '../../utils/useScreenSize';
import LabelledTextField from '../../components/LabelledTextField/LabelledTextField';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { password } from '../../validators';
import NotificationAlert, { Alert } from '../../components/NotificationAlert/NotificationAlert';

interface DispatchProps {
  login: typeof login;
}

interface SetPasswordPageProps extends DispatchProps {
}

interface SetNewPassword {
  password: string;
  confirmPassword: string;
}

const loginFormValidator = yup.object<SetNewPassword>({
  password: password(),
  confirmPassword: password().oneOf([yup.ref('password'), null], 'Passwords must match'),
});

const NewSetPasswordPage: React.FC<SetPasswordPageProps> = (props) => {
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [alert, setAlert] = useState<Alert | undefined>();
  const { t } = useTranslation();
  const { router: { push }, match } = useRouter();
  const { token } = match.location.query;
  const { isXsScreen } = useScreenSize();
  const {
    errors,
    getValues,
    register,
    formState,
  } = useForm<SetNewPassword>({ mode: 'onBlur', validationSchema: loginFormValidator });

  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      return;
    }
    services.user.getUserByToken({ resetPasswordToken: token })
      .then((user) => setIsValidToken(user.hasValidToken))
      .catch(() => setIsValidToken(false));
  }, [token]);

  const handleSetNewPassword = async () => {
    if (Object.keys(errors).length > 0) return;
    if (!isValidToken) {
      setAlert({
        message: t('Your password reset link is invalid'),
        severity: 'error',
      });
      return;
    }
    const values = getValues();
    try {
      const { email } = await services.user.resetPassword(values.password, values.confirmPassword, token || '');
      await props.login(email, values.password);
      push(`/${routes.login.root}`);
    } catch (error: any) {
      setAlert({
        message: t('An error occurred while creating a new password. Check your internet connection and try again.'),
        severity: 'error',
      });
      return;
    }
  };

  const renderAlertComponent = (alert: Alert) => {
    return (
      <NotificationAlert
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert?.(undefined)}
        alertSx={{ width: '100%', marginLeft: spaces.md, marginRight: spaces.md }}
      />
    );
  };

  return (
    <RedirectIfLoggedIn>
      {alert && renderAlertComponent(alert)}
      <PageContainer pageBodySx={{ gap: 0 }}>
        <Box 
          data-testid='new-set-password-page'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: isXsScreen ? spaces.xl : spaces.xxxl,
          }}>
          <OnboardingHeader 
            title={t('Set new password')}
            subtitle={t('The password must be at least 6 characters long.')}
          />
          <Box
            sx={{
              height: 'fit-content',
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              gap: spaces.md,
            }}
            >
              <FormControl fullWidth sx={{ gap: spaces.md }}>
                <LabelledTextField
                  dataTestId='set-new-password'
                  name='password'
                  label={t('New Password *')}
                  inputRef={register}
                  error={errors.password}
                  isPasswordTextField
                />
                <LabelledTextField
                  dataTestId='confirm-new-password'
                  name='confirmPassword'
                  label={t('Confirm New Password *')}
                  inputRef={register}
                  error={errors.confirmPassword}
                  isPasswordTextField
                />
                <Button
                  data-testid='new-password-submit-button'
                  variant='contained'
                  size='large'
                  color='primary'
                  type='submit'
                  sx={{
                    width: '100%',
                  }}
                  onClick={handleSetNewPassword}
                  disabled={formState.isSubmitting}
                >
                  {t('Update password')}
                </Button>
              </FormControl>
            </Box>
          </Box>
        </Box>
        <Button
          variant='text'
          size='medium'
          color='secondary'
          onClick={() => { push(`/${routes.login.root}`); } }
          data-testid='back-to-login-button'
          sx={{ marginTop: 'auto' }}
        >
          {t('Back to login')}
        </Button>
        <FooterLegalNavigation isSmallScreen={isXsScreen} />
      </PageContainer>
    </RedirectIfLoggedIn>
  );
};

const mapDispatch = {
  login,
};

export default compose<React.FC>(
  connect(undefined, mapDispatch),
  withRouter,
)(NewSetPasswordPage);
